import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { X } from 'lucide-react';

import { cn } from '@/lib/cn';

interface ToastShape {
  id: string;
  message: string;
  /** Optional undo action; firing it dismisses the toast. */
  onUndo?: () => void | Promise<void>;
  /** Soft tone. 'neutral' default; 'warn' for amber. */
  tone?: 'neutral' | 'warn';
  /** ms before auto-dismiss. Defaults to 7000 — longer than the
   *  conventional 5s so caregivers with older eyes have time to react. */
  durationMs?: number;
}

interface ToastContextValue {
  push: (toast: Omit<ToastShape, 'id'>) => void;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

export function ToastProvider({ children }: { children: ReactNode }): JSX.Element {
  const [toasts, setToasts] = useState<ToastShape[]>([]);
  const timersRef = useRef<Map<string, number>>(new Map());

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const handle = timersRef.current.get(id);
    if (handle) {
      window.clearTimeout(handle);
      timersRef.current.delete(id);
    }
  }, []);

  const push = useCallback(
    (toast: Omit<ToastShape, 'id'>) => {
      const id = crypto.randomUUID();
      const duration = toast.durationMs ?? 7000;
      setToasts((prev) => [...prev, { ...toast, id }]);
      const handle = window.setTimeout(() => dismiss(id), duration);
      timersRef.current.set(id, handle);
    },
    [dismiss],
  );

  useEffect(() => {
    return () => {
      timersRef.current.forEach((h) => window.clearTimeout(h));
      timersRef.current.clear();
    };
  }, []);

  return (
    <ToastContext.Provider value={{ push, dismiss }}>
      {children}
      <div
        aria-live="polite"
        aria-atomic="true"
        className="pointer-events-none fixed inset-x-0 bottom-24 z-50 flex flex-col items-center gap-2 px-4 md:bottom-8"
      >
        {toasts.map((t) => (
          <div
            key={t.id}
            role="status"
            className={cn(
              'pointer-events-auto flex w-full max-w-sm items-center gap-3 rounded-lg px-4 py-3 text-sm shadow-md',
              t.tone === 'warn'
                ? 'border border-amber-200 bg-amber-50 text-amber-900'
                : 'border border-border bg-foreground text-white',
            )}
          >
            <span className="flex-1">{t.message}</span>
            {t.onUndo && (
              <button
                type="button"
                className={cn(
                  'rounded-pill px-3 py-1 text-xs font-semibold',
                  t.tone === 'warn'
                    ? 'bg-white text-amber-900 hover:bg-amber-100'
                    : 'bg-white text-foreground hover:bg-muted',
                )}
                onClick={() => {
                  void t.onUndo?.();
                  dismiss(t.id);
                }}
              >
                Undo
              </button>
            )}
            <button
              type="button"
              aria-label="Dismiss"
              onClick={() => dismiss(t.id)}
              className={cn(
                'rounded p-1 opacity-70 hover:opacity-100',
                t.tone === 'warn' ? 'text-amber-900' : 'text-white',
              )}
            >
              <X size={14} />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}
